import React, { useCallback, useEffect } from 'react';
import { useAvailabilityStore, useAvailabilityColumnStore, useInventoryFilterStore, useUIStore } from '@/stores';
import type { AvailabilityFilters } from '@/stores/availability/availability-store';
import { logger } from '@/utils/logger';
import { recordExportEvent } from '@/services/audit/audit-service';
import {
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { BaseFilterSidebar } from '@/components/ui/BaseFilterSidebar';
import { CollapsibleFilterSection } from '@/features/inventory/components/CollapsibleFilterSection';
import { InventoryState } from '@/types';
import { AvailabilityColumnManagerPanel } from './AvailabilityColumnManagerPanel';
import { AVAILABILITY_COLUMN_ID_TO_FILTER_ID } from '@/utils/availability/filter-column-mapping';
import { clsx } from 'clsx';
import { sortFiltersByColumnOrder as sortFilters } from '@/utils/filter-utils';
import { getDefaultStateId } from '@/utils/inventory/intraday-state-utils';
import { toUtcDateKey } from '@/utils/date-key';

/** Reverse mapping: filter section ID → table column ID. */
const FILTER_ID_TO_COLUMN_ID: Record<string, string> = Object.fromEntries(
  Object.entries(AVAILABILITY_COLUMN_ID_TO_FILTER_ID).map(([colId, filterId]) => [filterId, colId])
);

type FilterDef = { id: string; label: string };

const COLLAPSIBLE_FILTER_DEFS: FilterDef[] = [
  { id: 'quantity', label: 'Quantity' },
  { id: 'counterparties', label: 'Counterparty' },
  { id: 'caStatuses', label: 'CA Status' },
  { id: 'benchmarkFeeThreshold', label: 'Benchmark Fee Threshold' },
];

const sortFiltersByColumnOrder = (filters: FilterDef[], columns: { id: string }[]) =>
  sortFilters(filters, columns, FILTER_ID_TO_COLUMN_ID);

interface FilterSidebarProps {
  counterparties?: string[];
  caStatuses?: string[];
  filteredDataCount?: number;
  currentStateName?: string;
  statesData?: InventoryState[];
  isStatesLoading?: boolean;
  isDataLoading?: boolean;
  hasError?: boolean;
  onExport?: () => void;
}

type SidebarTab = 'filters' | 'columns';

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  counterparties = [],
  caStatuses = [],
  filteredDataCount = 0,
  statesData = [],
  isStatesLoading = false,
  isDataLoading = false,
  hasError = false,
  onExport,
}) => {
  const inventoryFilters = useInventoryFilterStore((state) => state.inventoryFilters);
  const setInventoryFilters = useInventoryFilterStore((state) => state.setInventoryFilters);

  const availabilityFilters = useAvailabilityStore((state) => state.availabilityFilters);
  const setAvailabilityFilters = useAvailabilityStore((state) => state.setAvailabilityFilters);
  const clearAvailabilityFilters = useAvailabilityStore((state) => state.clearAvailabilityFilters);

  const filterSidebarCollapsed = useUIStore((state) => state.filterSidebarCollapsed);
  const activeTab = useUIStore((s) => s.sidebarActiveTab) as SidebarTab;
  const setSidebarActiveTab = useUIStore((s) => s.setSidebarActiveTab);
  const setColumnsTabActive = useUIStore((state) => state.setColumnsTabActive);
  const focusedFilterId = useUIStore((state) => state.focusedFilterId);
  const setFocusedFilterId = useUIStore((state) => state.setFocusedFilterId);
  const expandedFilterIds = useUIStore((state) => state.expandedFilterIds);
  const expandFilter = useUIStore((state) => state.expandFilter);
  const collapseFilter = useUIStore((state) => state.collapseFilter);
  const expandAllFilters = useUIStore((state) => state.expandAllFilters);
  const collapseAllFilters = useUIStore((state) => state.collapseAllFilters);
  const setLocateColumnRequest = useUIStore((state) => state.setLocateColumnRequest);
  const columnConfig = useAvailabilityColumnStore((state) => state.columns);

  // Auto-clear focus after 2s
  useEffect(() => {
    if (focusedFilterId == null) return;
    // Switch to filters tab when a filter is focused
    if (activeTab !== 'filters') {
      setSidebarActiveTab('filters');
      setColumnsTabActive(false);
    }
    const t = setTimeout(() => setFocusedFilterId(null), 2000);
    return () => clearTimeout(t);
  }, [focusedFilterId, setFocusedFilterId, activeTab, setSidebarActiveTab, setColumnsTabActive]);

  const handleTabChange = useCallback((tab: SidebarTab) => {
    setSidebarActiveTab(tab);
    setColumnsTabActive(tab === 'columns');
  }, [setSidebarActiveTab, setColumnsTabActive]);

  const updateFilter = useCallback(<K extends keyof AvailabilityFilters>(key: K, value: AvailabilityFilters[K]) => {
    setAvailabilityFilters({
      ...availabilityFilters,
      [key]: value,
    });
  }, [availabilityFilters, setAvailabilityFilters]);

  // Clear all delegates to the store so every reset path shares the single
  // defaultFilters source — a hand-written literal here drifted off-default
  // (accountForClientTransfers: false vs default true, LR-598).
  const clearAllFilters = clearAvailabilityFilters;

  const handleDownloadDailyReport = useCallback(async () => {
    try {
      const selectedState = statesData?.find(state => state.id === inventoryFilters.state);
      // Statement = "Real time" (the default) leaves inventoryFilters.state undefined,
      // so no state is selected. Fall back to today's morning state instead of
      // bailing out silently (LR-405).
      // Match by UTC day: snapshots are stamped in UTC (evening at 22:00 UTC), so
      // local-day comparison shifts a 22:00-UTC selection onto the next day for
      // viewers east of UTC and picks the wrong day's morning (LR-485).
      const targetDateKey = toUtcDateKey(selectedState ? new Date(selectedState.created_at) : new Date());
      const morningState = statesData?.find(state =>
        toUtcDateKey(new Date(state.created_at)) === targetDateKey && state.type === 'morning'
      );
      if (!morningState) {
        alert('Morning state not found for the selected date.');
        return;
      }
      const { inventoryService } = await import('@/services/inventory/inventory-service');
      const { groupInventoryBySymbol } = await import('@/utils/inventory/data-transformers');

      const countData = await inventoryService.getInventoryCount({ state: morningState.id });
      const allInventory = await inventoryService.getAllInventory({ state: morningState.id }, countData.count);
      const availabilityData = groupInventoryBySymbol(allInventory, 0);

      const sortedData = [...availabilityData].sort((a, b) => b.quantity - a.quantity);

      const { default: Papa } = await import('papaparse');
      const csvData = sortedData.map((row) => ({
        'Symbol ID': row.symbol_id,
        'SEDOL': row.sedol,
        'ISIN': row.isin,
        'Quantity': row.quantity,
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `daily-availability-report-${targetDateKey}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      recordExportEvent('availability.export_csv', { row_count: csvData.length });
    } catch (error) {
      logger.error('Failed to download daily availability report:', error);
      alert('Failed to download daily availability report. Please try again.');
    }
  }, [statesData, inventoryFilters.state]);

  const activeFilterCount =
    (availabilityFilters.quantityMin !== null ? 1 : 0) +
    (availabilityFilters.quantityMax !== null ? 1 : 0) +
    (availabilityFilters.counterparties.length > 0 ? 1 : 0) +
    (availabilityFilters.caStatuses.length > 0 ? 1 : 0) +
    (availabilityFilters.accountForClientTransfers ? 1 : 0) +
    (availabilityFilters.benchmarkFeeThreshold !== 0 ? 1 : 0) +
    (!availabilityFilters.excludeUpcomingCA ? 1 : 0) +
    (!availabilityFilters.hideISINNone ? 1 : 0);

  const headerContent = (
    <div className="flex items-center justify-between w-full ml-4">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => handleTabChange('filters')}
          className={clsx(
            'px-2.5 py-1 text-base font-semibold transition-colors',
            activeTab === 'filters'
              ? 'text-gray-900 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Filters
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('columns')}
          className={clsx(
            'px-2.5 py-1 text-base font-semibold transition-colors',
            activeTab === 'columns'
              ? 'text-gray-900 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Columns
        </button>
      </div>
    </div>
  );

  const hasActiveForId = (id: string) => {
    switch (id) {
      case 'quantity': return availabilityFilters.quantityMin !== null || availabilityFilters.quantityMax !== null;
      case 'counterparties': return availabilityFilters.counterparties.length > 0;
      case 'caStatuses': return availabilityFilters.caStatuses.length > 0;
      case 'benchmarkFeeThreshold': return availabilityFilters.benchmarkFeeThreshold !== null && availabilityFilters.benchmarkFeeThreshold !== 0;
      default: return false;
    }
  };

  return (
    <BaseFilterSidebar
      activeFilterCount={activeFilterCount}
      onClearAll={clearAllFilters}
      headerContent={headerContent}
    >
      {/* Columns tab content */}
      {!filterSidebarCollapsed && activeTab === 'columns' && (
        <div className="flex flex-1 flex-col -mt-2 min-h-0">
          <AvailabilityColumnManagerPanel />
        </div>
      )}

      {/* Filters tab content */}
      {!filterSidebarCollapsed && activeTab === 'filters' && (
        <>
          {/* Daily Availability Report Button */}
          <div className="-mt-2">
            <button
              onClick={handleDownloadDailyReport}
              disabled={isStatesLoading || isDataLoading || hasError}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Daily Availability Report
            </button>
            <p className="mt-2 text-xs text-gray-500 text-left">
              Generated daily at 8 AM for client distribution
            </p>
          </div>

          {/* Expand / Collapse All */}
          {(() => {
            const sortedFilters = sortFiltersByColumnOrder(COLLAPSIBLE_FILTER_DEFS, columnConfig);
            const collapsibleIds = sortedFilters.map((f) => f.id);
            const allExpanded = collapsibleIds.every(
              (id) => expandedFilterIds.includes(id) || hasActiveForId(id)
            );
            return (
              <div className="flex items-center justify-between -mt-2 mb-1">
                <button
                  type="button"
                  onClick={() => {
                    if (allExpanded) {
                      collapseAllFilters();
                    } else {
                      expandAllFilters(collapsibleIds);
                    }
                  }}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </button>
              </div>
            );
          })()}

          {/* Statement Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Statement</h3>
            <DateTimePicker
              value={inventoryFilters.state || getDefaultStateId(statesData || []) || 0}
              states={statesData}
              onChange={(stateId) => {
                setInventoryFilters({
                  ...inventoryFilters,
                  state: stateId,
                });
              }}
              disabled={isStatesLoading || isDataLoading || hasError}
            />
          </div>

          {/* Column-ordered collapsible filters */}
          {sortFiltersByColumnOrder(COLLAPSIBLE_FILTER_DEFS, columnConfig).map((filterDef) => {
            const filterId = filterDef.id;
            const colId = FILTER_ID_TO_COLUMN_ID[filterId] ?? null;
            const hasActive = hasActiveForId(filterId);
            const expanded = expandedFilterIds.includes(filterId) || hasActive;
            const isColVisible = colId ? columnConfig.some((c) => c.id === colId && c.visible) : false;

            const renderContent = () => {
              switch (filterId) {
                case 'quantity':
                  return (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Min</label>
                        <input
                          type="number"
                          step="1"
                          value={availabilityFilters.quantityMin ?? ''}
                          onChange={(e) => updateFilter('quantityMin', e.target.value ? parseInt(e.target.value) : null)}
                          onBlur={() => {
                            if (availabilityFilters.quantityMin !== null && availabilityFilters.quantityMax !== null && availabilityFilters.quantityMin > availabilityFilters.quantityMax) {
                              updateFilter('quantityMin', availabilityFilters.quantityMax);
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Max</label>
                        <input
                          type="number"
                          step="1"
                          value={availabilityFilters.quantityMax ?? ''}
                          onChange={(e) => updateFilter('quantityMax', e.target.value ? parseInt(e.target.value) : null)}
                          onBlur={() => {
                            if (availabilityFilters.quantityMax !== null && availabilityFilters.quantityMin !== null && availabilityFilters.quantityMax < availabilityFilters.quantityMin) {
                              updateFilter('quantityMax', availabilityFilters.quantityMin);
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          placeholder="1000000"
                        />
                      </div>
                    </div>
                  );
                case 'counterparties':
                  return (
                    <MultiSelectDropdown
                      options={counterparties}
                      selectedValues={availabilityFilters.counterparties}
                      onChange={(values) => updateFilter('counterparties', values)}
                      placeholder="Select counterparties..."
                      searchPlaceholder="Search counterparties..."
                      pinSelectedToTop={true}
                      showClearAll={false}
                    />
                  );
                case 'caStatuses':
                  return (
                    <MultiSelectDropdown
                      options={caStatuses}
                      selectedValues={availabilityFilters.caStatuses}
                      onChange={(values) => updateFilter('caStatuses', values)}
                      placeholder="Select CA statuses..."
                      searchPlaceholder="Search CA statuses..."
                      pinSelectedToTop={true}
                      showClearAll={false}
                    />
                  );
                case 'benchmarkFeeThreshold':
                  return (
                    <>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={availabilityFilters.benchmarkFeeThreshold !== null && availabilityFilters.benchmarkFeeThreshold !== undefined ? `${availabilityFilters.benchmarkFeeThreshold}%` : '0%'}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            const numericValue = inputValue.replace('%', '').trim();
                            if (numericValue === '') {
                              updateFilter('benchmarkFeeThreshold', 0);
                            } else {
                              const parsed = parseFloat(numericValue);
                              if (!isNaN(parsed)) {
                                updateFilter('benchmarkFeeThreshold', parsed);
                              }
                            }
                          }}
                          onBlur={(e) => {
                            const inputValue = e.target.value;
                            if (!inputValue.includes('%')) {
                              const numericValue = inputValue.trim();
                              if (numericValue === '') {
                                updateFilter('benchmarkFeeThreshold', 0);
                              } else {
                                const parsed = parseFloat(numericValue);
                                if (!isNaN(parsed)) {
                                  updateFilter('benchmarkFeeThreshold', parsed);
                                }
                              }
                            }
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0%"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Items with Benchmark Fee below this threshold will be filtered out
                      </p>
                    </>
                  );
                default:
                  return null;
              }
            };

            return (
              <CollapsibleFilterSection
                key={filterId}
                filterId={filterId}
                label={filterDef.label}
                expanded={expanded}
                onToggle={() => (expanded && !hasActive ? collapseFilter(filterId) : expandFilter(filterId))}
                isFocused={focusedFilterId === filterId}
                hasActiveValues={hasActive}
                columnId={colId}
                onLocateColumn={(id) => setLocateColumnRequest({ columnId: id, trigger: Date.now() })}
                isColumnVisible={isColVisible}
              >
                {renderContent()}
              </CollapsibleFilterSection>
            );
          })}

          {/* Exclude positions with upcoming CA */}
          <div className="flex items-center gap-2">
            <input
              id="exclude-ca-checkbox"
              type="checkbox"
              checked={availabilityFilters.excludeUpcomingCA}
              onChange={(e) => updateFilter('excludeUpcomingCA', e.target.checked)}
              className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="exclude-ca-checkbox" className="text-sm text-gray-700 cursor-pointer group relative">
              <span>Exclude positions with upcoming CA</span>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 z-50">
                Filter out positions with pending corporate actions
              </div>
            </label>
          </div>

          {/* Account for Client Transfers Filter */}
          <div className="flex items-center gap-2">
            <input
              id="account-for-client-transfers-checkbox"
              type="checkbox"
              checked={availabilityFilters.accountForClientTransfers}
              onChange={(e) => updateFilter('accountForClientTransfers', e.target.checked)}
              className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="account-for-client-transfers-checkbox" className="text-sm text-gray-700 cursor-pointer group relative">
              <span>Account for client transfers</span>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 z-50">
                Subtract pending out transfers from available quantity
              </div>
            </label>
          </div>

          {/* Hide entries with ISIN "none" */}
          <div className="flex items-center gap-2">
            <input
              id="hide-isin-none-checkbox"
              type="checkbox"
              checked={availabilityFilters.hideISINNone}
              onChange={(e) => updateFilter('hideISINNone', e.target.checked)}
              className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="hide-isin-none-checkbox" className="text-sm text-gray-700 cursor-pointer">
              Hide entries with ISIN "none"
            </label>
          </div>

          {/* Exclude internal columns */}
          <div className="flex items-center gap-2">
            <input
              id="exclude-internal-columns-checkbox"
              type="checkbox"
              checked={availabilityFilters.excludeInternalColumns}
              onChange={(e) => updateFilter('excludeInternalColumns', e.target.checked)}
              className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="exclude-internal-columns-checkbox" className="text-sm text-gray-700 cursor-pointer">
              Exclude internal columns (CA, Transfers) not intended for clients
            </label>
          </div>

          {/* Export Button */}
          <div>
            <button
              onClick={onExport}
              disabled={isDataLoading || hasError}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download Filtered Report
            </button>
            <p className="mt-2 text-xs text-gray-500 text-center">
              {filteredDataCount} row{filteredDataCount !== 1 ? 's' : ''} will be exported
            </p>
          </div>
        </>
      )}
    </BaseFilterSidebar>
  );
};
